import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popper,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';

import styles from './styles';

const useStyles = makeStyles(styles);

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Search input; with `onSelect` it also renders a typeahead dropdown the caller filters and ranks. */
const SearchField = ({
  value,
  onChange,
  options,
  onSelect,
  placeholder,
  loading,
  noOptionsText,
  clearable,
  disabled,
  className,
}) => {
  const classes = useStyles();
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);

  const highlight = (label) => {
    if (!value) return label;
    return label.split(new RegExp(`(${escapeRegExp(value)})`, 'gi')).map((part, index) =>
      index % 2 ? (
        <span className={classes.match} key={index}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const select = (option) => {
    setOpen(false);
    if (onSelect) onSelect(option);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className={classnames(classes.root, className)} ref={anchor}>
        <TextField
          fullWidth
          variant="outlined"
          className={classes.field}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          inputProps={{ 'aria-label': placeholder }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={classes.searchIcon} />
              </InputAdornment>
            ),
            endAdornment: clearable && !!value && !disabled && (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="clear search" onClick={() => onChange('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {/* Without onSelect there is nothing to pick, so the field stays a plain search input. */}
        {onSelect && (
          <Popper
            open={open}
            anchorEl={anchor.current}
            placement="bottom-start"
            disablePortal
            className={classes.popper}
          >
            <Paper className={classes.paper}>
              {loading && (
                <div className={classes.placeholder}>
                  <CircularProgress size={24} />
                </div>
              )}
              {!loading &&
                options.map((option) => (
                  <MenuItem
                    key={option.value}
                    className={classes.option}
                    onClick={() => select(option)}
                  >
                    {highlight(option.label)}
                  </MenuItem>
                ))}
              {!loading && !options.length && (
                <div className={classes.placeholder}>{noOptionsText}</div>
              )}
            </Paper>
          </Popper>
        )}
      </div>
    </ClickAwayListener>
  );
};

SearchField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  noOptionsText: PropTypes.string,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

SearchField.defaultProps = {
  options: [],
  onSelect: undefined,
  placeholder: 'Search',
  loading: false,
  noOptionsText: 'No matches found',
  clearable: true,
  disabled: false,
  className: undefined,
};

export default SearchField;
